export type RenderMode<T extends string> = { hidden: boolean; auto: boolean; manualMode: T };

export type ManualRenderModes<TRenderMode> = TRenderMode extends RenderMode<infer X> ? X : never;

/**
 * A string representation of the current value of a render mode. This is a lossy representation of a render mode as it
 *   does not contain historical information needed for smooth transitions between automatic modes.
 */
export type RenderModeString<T extends RenderMode<any>> = ManualRenderModes<T> | 'auto' | 'none';

/**
 * An easy type-safe way to create an automatic render mode.
 */
export function automaticRenderMode<T extends string>(
  defaultManualMode: T,
  previousMode?: RenderMode<T>,
): RenderMode<T> {
  return {
    auto: true,
    hidden: previousMode?.hidden ?? false,
    manualMode: previousMode?.manualMode ?? defaultManualMode,
  };
}

/**
 * Creates a `RenderModeString` to represent the current value of a given `RenderMode`.
 */
export function getRenderModeString<T extends string>(mode: RenderMode<T>): RenderModeString<RenderMode<T>> {
  if (mode.hidden) return 'none';
  if (mode.auto) return 'auto';
  return mode.manualMode;
}

/**
 * Returns a new `RenderMode` based on the desired mode and the current value of the render mode.
 */
export function setRenderMode<T extends string>(
  desiredMode: T | 'auto' | 'none',
  oldValue: RenderMode<T>,
): RenderMode<T> {
  if (desiredMode === 'auto') {
    return { ...oldValue, hidden: false, auto: true };
  }

  if (desiredMode === 'none') {
    return { ...oldValue, hidden: true };
  }

  return { hidden: false, auto: false, manualMode: desiredMode };
}
